import { type Language } from '../i18n';

interface StartupReportDisplayProps {
  language: Language;
  report: StartupReport | null;
  isLoading: boolean;
  onNewAssessment: () => void;
}

interface DimensionScore {
  score: number;
  assessment: string;
}

interface StrategicDirection {
  rank: number;
  direction: string;
  description: string;
  pros?: string[];
  cons?: string[];
  recommended_first_step?: string;
}

interface KeyRisk {
  risk: string;
  severity: string;
  mitigation: string;
}

interface ImmediateAction {
  action: string;
  rationale: string;
  timeline?: string;
}

interface StartupReport {
  metadata: {
    assessment_date: string;
    language: string;
    startup_profile: {
      idea_summary: string;
      project_stage: string;
      target_sector: string;
      founder_type: string;
      team_size: string;
    };
  };
  executive_summary?: string[];
  scores: {
    composite_score: number;
    viability_rating: string;
    dimensions: Record<string, DimensionScore>;
  };
  analysis: {
    core_problem: string;
    target_market: string;
    market_size_estimate?: string;
    competitive_landscape?: string;
    unique_advantage?: string;
  };
  strategic_directions: StrategicDirection[];
  technology_roadmap: {
    recommended_approach: string;
    approach_rationale?: string;
    mvp_scope?: {
      description: string;
      core_features: string[];
      estimated_complexity: string;
      estimated_timeline?: string;
    };
    phases?: Record<string, {
      name: string;
      duration: string;
      deliverables: string[];
    }>;
  };
  strengths_and_risks: {
    key_strengths: string[];
    key_risks: KeyRisk[];
    adjacent_markets?: string[];
  };
  recommendations: {
    immediate_actions: ImmediateAction[];
    validation_steps?: string[];
    key_questions_to_answer?: string[];
  };
  summary: {
    headline: string;
    elevator_pitch?: string;
    primary_recommendation: string;
    partnership_value?: string;
  };
}

const DIMENSION_LABELS: Record<string, Record<string, string>> = {
  problem_clarity: { it: 'Chiarezza del Problema', en: 'Problem Clarity' },
  market_opportunity: { it: 'Opportunità di Mercato', en: 'Market Opportunity' },
  founder_market_fit: { it: 'Founder-Market Fit', en: 'Founder-Market Fit' },
  solution_viability: { it: 'Viabilità Soluzione', en: 'Solution Viability' },
  technical_feasibility: { it: 'Fattibilità Tecnica', en: 'Technical Feasibility' },
  business_model: { it: 'Modello di Business', en: 'Business Model' },
  competitive_position: { it: 'Posizione Competitiva', en: 'Competitive Position' },
  execution_readiness: { it: 'Prontezza Esecuzione', en: 'Execution Readiness' },
  pivot_potential: { it: 'Potenziale Pivot', en: 'Pivot Potential' },
};

function ScoreBar({ score, max = 5 }: { score: number; max?: number }) {
  const pct = (score / max) * 100;
  const color = pct >= 70 ? 'bg-green-500' : pct >= 50 ? 'bg-yellow-500' : 'bg-red-500';
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color} transition-all`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-sm font-medium text-foreground w-8 text-right">{score.toFixed(1)}</span>
    </div>
  );
}

function CompositeScore({ score, rating }: { score: number; rating: string }) {
  const color = score >= 80 ? 'text-green-500' : score >= 65 ? 'text-blue-500' : score >= 50 ? 'text-yellow-500' : score >= 35 ? 'text-orange-500' : 'text-red-500';
  return (
    <div className="text-center">
      <div className={`text-5xl font-bold ${color}`}>{Math.round(score)}</div>
      <div className="text-sm text-muted-foreground mt-1">/100</div>
      <div className={`text-lg font-semibold mt-2 ${color}`}>{rating}</div>
    </div>
  );
}

function SeverityBadge({ severity }: { severity: string }) {
  const colors: Record<string, string> = {
    low: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    medium: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
    high: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    basso: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    medio: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
    alto: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  };
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${colors[severity.toLowerCase()] ?? 'bg-muted text-muted-foreground'}`}>
      {severity}
    </span>
  );
}

export function StartupReportDisplay({ language, report, isLoading, onNewAssessment }: StartupReportDisplayProps) {
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4" />
        <p className="text-muted-foreground">
          {language === 'it' ? 'Generazione del report in corso...' : 'Generating report...'}
        </p>
      </div>
    );
  }

  if (!report) return null;

  const it = language === 'it';

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Executive Summary */}
      {report.executive_summary && report.executive_summary.length > 0 && (
        <div className="card p-4 border-l-4 border-l-primary bg-primary/5">
          <h2 className="text-sm font-semibold text-primary mb-2">
            {it ? 'Sintesi Esecutiva' : 'Executive Summary'}
          </h2>
          <ul className="space-y-1.5">
            {report.executive_summary.map((item, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-foreground">
                <span className="text-primary font-bold mt-0.5">-</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Header & Composite Score */}
      <div className="card p-6">
        <h1 className="text-2xl font-bold text-foreground mb-2">{report.summary.headline}</h1>
        {report.summary.elevator_pitch && (
          <p className="text-muted-foreground mb-6">{report.summary.elevator_pitch}</p>
        )}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
          <CompositeScore score={report.scores.composite_score} rating={report.scores.viability_rating} />
          <div className="md:col-span-2 space-y-2">
            <h3 className="text-sm font-medium text-foreground">
              {it ? 'Analisi del Problema' : 'Problem Analysis'}
            </h3>
            <p className="text-sm text-muted-foreground">{report.analysis.core_problem}</p>
            <h3 className="text-sm font-medium text-foreground mt-3">
              {it ? 'Mercato Target' : 'Target Market'}
            </h3>
            <p className="text-sm text-muted-foreground">{report.analysis.target_market}</p>
            {report.analysis.market_size_estimate && (
              <>
                <h3 className="text-sm font-medium text-foreground mt-3">
                  {it ? 'Stima Dimensione Mercato' : 'Market Size Estimate'}
                </h3>
                <p className="text-sm text-muted-foreground">{report.analysis.market_size_estimate}</p>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Dimension Scores */}
      <div className="card p-6">
        <h2 className="text-lg font-semibold text-foreground mb-4">
          {it ? 'Punteggi per Dimensione' : 'Dimension Scores'}
        </h2>
        <div className="space-y-3">
          {Object.entries(report.scores.dimensions).map(([key, dim]) => (
            <div key={key}>
              <div className="flex justify-between items-center mb-1">
                <span className="text-sm text-foreground">
                  {DIMENSION_LABELS[key]?.[language] ?? key}
                </span>
              </div>
              <ScoreBar score={dim.score} />
              {dim.assessment && (
                <p className="text-xs text-muted-foreground mt-1">{dim.assessment}</p>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Strategic Directions */}
      {report.strategic_directions.length > 0 && (
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-foreground mb-4">
            {it ? 'Direzioni Strategiche' : 'Strategic Directions'}
          </h2>
          <div className="space-y-4">
            {report.strategic_directions.map((dir, i) => (
              <div key={i} className="border border-border rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs font-bold bg-primary/10 text-primary px-2 py-0.5 rounded">
                    #{dir.rank}
                  </span>
                  <h3 className="font-medium text-foreground">{dir.direction}</h3>
                </div>
                <p className="text-sm text-muted-foreground mb-3">{dir.description}</p>
                {dir.pros && dir.pros.length > 0 && (
                  <div className="mb-2">
                    <span className="text-xs font-medium text-green-600">Pro:</span>
                    <ul className="text-xs text-muted-foreground ml-4 list-disc">
                      {dir.pros.map((p, j) => <li key={j}>{p}</li>)}
                    </ul>
                  </div>
                )}
                {dir.cons && dir.cons.length > 0 && (
                  <div className="mb-2">
                    <span className="text-xs font-medium text-red-600">Contro:</span>
                    <ul className="text-xs text-muted-foreground ml-4 list-disc">
                      {dir.cons.map((c, j) => <li key={j}>{c}</li>)}
                    </ul>
                  </div>
                )}
                {dir.recommended_first_step && (
                  <p className="text-xs text-primary mt-2">
                    {it ? 'Primo passo:' : 'First step:'} {dir.recommended_first_step}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Technology Roadmap */}
      <div className="card p-6">
        <h2 className="text-lg font-semibold text-foreground mb-4">
          {it ? 'Roadmap Tecnologica' : 'Technology Roadmap'}
        </h2>
        <div className="mb-4">
          <span className="text-xs font-medium bg-accent/10 text-accent px-2 py-1 rounded">
            {it ? 'Approccio:' : 'Approach:'} {report.technology_roadmap.recommended_approach}
          </span>
          {report.technology_roadmap.approach_rationale && (
            <p className="text-sm text-muted-foreground mt-2">{report.technology_roadmap.approach_rationale}</p>
          )}
        </div>
        {report.technology_roadmap.mvp_scope && (
          <div className="border border-border rounded-lg p-4 mb-4">
            <h3 className="font-medium text-foreground text-sm mb-2">MVP Scope</h3>
            <p className="text-sm text-muted-foreground mb-2">{report.technology_roadmap.mvp_scope.description}</p>
            <ul className="text-xs text-muted-foreground list-disc ml-4 mb-2">
              {report.technology_roadmap.mvp_scope.core_features.map((f, i) => <li key={i}>{f}</li>)}
            </ul>
            <div className="flex gap-4 text-xs">
              <span className="text-muted-foreground">
                {it ? 'Complessità:' : 'Complexity:'}{' '}
                <SeverityBadge severity={report.technology_roadmap.mvp_scope.estimated_complexity} />
              </span>
              {report.technology_roadmap.mvp_scope.estimated_timeline && (
                <span className="text-muted-foreground">
                  Timeline: {report.technology_roadmap.mvp_scope.estimated_timeline}
                </span>
              )}
            </div>
          </div>
        )}
        {report.technology_roadmap.phases && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {Object.entries(report.technology_roadmap.phases).map(([key, phase]) => (
              <div key={key} className="border border-border rounded-lg p-3">
                <h4 className="font-medium text-foreground text-sm">{phase.name}</h4>
                <p className="text-xs text-muted-foreground mb-2">{phase.duration}</p>
                <ul className="text-xs text-muted-foreground list-disc ml-3">
                  {phase.deliverables.map((d, i) => <li key={i}>{d}</li>)}
                </ul>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Strengths & Risks */}
      <div className="card p-6">
        <h2 className="text-lg font-semibold text-foreground mb-4">
          {it ? 'Punti di Forza e Rischi' : 'Strengths & Risks'}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-sm font-medium text-green-600 mb-2">
              {it ? 'Punti di Forza' : 'Key Strengths'}
            </h3>
            <ul className="space-y-1">
              {report.strengths_and_risks.key_strengths.map((s, i) => (
                <li key={i} className="text-sm text-muted-foreground flex items-start gap-2">
                  <span className="text-green-500 mt-1">+</span> {s}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="text-sm font-medium text-red-600 mb-2">
              {it ? 'Rischi Chiave' : 'Key Risks'}
            </h3>
            <div className="space-y-2">
              {report.strengths_and_risks.key_risks.map((r, i) => (
                <div key={i} className="text-sm">
                  <div className="flex items-center gap-2">
                    <SeverityBadge severity={r.severity} />
                    <span className="text-foreground">{r.risk}</span>
                  </div>
                  <p className="text-xs text-muted-foreground ml-4 mt-1">
                    {it ? 'Mitigazione:' : 'Mitigation:'} {r.mitigation}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
        {report.strengths_and_risks.adjacent_markets && report.strengths_and_risks.adjacent_markets.length > 0 && (
          <div className="mt-4 pt-4 border-t border-border">
            <h3 className="text-sm font-medium text-foreground mb-2">
              {it ? 'Mercati Adiacenti' : 'Adjacent Markets'}
            </h3>
            <div className="flex flex-wrap gap-2">
              {report.strengths_and_risks.adjacent_markets.map((m, i) => (
                <span key={i} className="text-xs bg-muted px-2 py-1 rounded-full text-muted-foreground">{m}</span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Recommendations */}
      <div className="card p-6">
        <h2 className="text-lg font-semibold text-foreground mb-4">
          {it ? 'Raccomandazioni' : 'Recommendations'}
        </h2>
        <div className="space-y-3">
          {report.recommendations.immediate_actions.map((a, i) => (
            <div key={i} className="border border-border rounded-lg p-3">
              <div className="flex items-start gap-2">
                <span className="text-primary font-bold text-sm">{i + 1}.</span>
                <div>
                  <p className="text-sm font-medium text-foreground">{a.action}</p>
                  <p className="text-xs text-muted-foreground mt-1">{a.rationale}</p>
                  {a.timeline && (
                    <span className="text-xs text-primary mt-1 inline-block">{a.timeline}</span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
        {report.recommendations.key_questions_to_answer && report.recommendations.key_questions_to_answer.length > 0 && (
          <div className="mt-4 pt-4 border-t border-border">
            <h3 className="text-sm font-medium text-foreground mb-2">
              {it ? 'Domande da Approfondire' : 'Questions to Explore'}
            </h3>
            <ul className="text-sm text-muted-foreground space-y-1 list-disc ml-4">
              {report.recommendations.key_questions_to_answer.map((q, i) => <li key={i}>{q}</li>)}
            </ul>
          </div>
        )}
      </div>

      {/* Primary Recommendation */}
      <div className="card p-6 bg-primary-50 dark:bg-primary-50/10 border-primary/20">
        <h2 className="text-lg font-semibold text-foreground mb-2">
          {it ? 'Prossimo Passo Consigliato' : 'Recommended Next Step'}
        </h2>
        <p className="text-muted-foreground">{report.summary.primary_recommendation}</p>
        {report.summary.partnership_value && (
          <p className="text-sm text-primary mt-3">{report.summary.partnership_value}</p>
        )}
      </div>

      {/* Actions */}
      <div className="flex gap-3">
        <button onClick={onNewAssessment} className="btn-primary">
          {it ? 'Nuova Valutazione' : 'New Assessment'}
        </button>
        <button
          onClick={() => {
            const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `startup-report-${report.metadata.assessment_date || 'report'}.json`;
            a.click();
            URL.revokeObjectURL(url);
          }}
          className="btn-secondary"
        >
          {it ? 'Scarica Report (JSON)' : 'Download Report (JSON)'}
        </button>
      </div>
    </div>
  );
}
